'use client';

import Link from 'next/link';
import { Container, Section } from '@/components/primitives';
import { BookCover } from '@/components/book-cover';
import { Icon } from '@/components/icons';
import { useAuth } from '@/lib/auth-context';
import type { Book } from '@/types/catalog';

export function HomeHero({ covers, total }: { covers: Book[]; total: number }) {
  const { loggedIn } = useAuth();

  const stats: Array<[string, string]> = [
    [total > 0 ? String(total) : '—', 'Títulos en el acervo'],
    ['30', 'Días por préstamo'],
    ['Gratuito', 'Sin costo, siempre'],
    ['32', 'Estados, todo México'],
  ];

  return (
    <Section pad="xl">
      <Container>
        <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 56, alignItems: 'end' }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 22 }}>Programa público de lectura</div>
            <h1
              className="serif"
              style={{
                margin: 0,
                fontSize: 'clamp(36px, 4.2vw, 60px)',
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                fontWeight: 600,
                textWrap: 'balance',
              }}
            >
              Leer es un derecho.
              <br />
              <em style={{ fontWeight: 400 }}>Bienvenidos a la Biblioteca Digital FCE.</em>
            </h1>
            <p style={{ marginTop: 24, color: 'var(--ink-soft)', fontSize: 17, maxWidth: 540, textWrap: 'pretty' }}>
              El catálogo del Fondo de Cultura Económica, gratuito y disponible para préstamo digital desde cualquier dispositivo.
            </p>
            <div style={{ marginTop: 36, display: 'flex', gap: 14, alignItems: 'center' }}>
              {loggedIn ? (
                <>
                  <Link href="/coleccion/narrativa" className="btn btn-primary" style={{ padding: '14px 24px' }}>
                    <Icon.book /> Comenzar a leer
                  </Link>
                  <Link href="/mi-estanteria" className="under" style={{ textDecoration: 'none', fontWeight: 500, color: 'var(--ink)' }}>
                    Ir a mi estantería →
                  </Link>
                </>
              ) : (
                <>
                  <Link href="/crear-cuenta" className="btn btn-primary" style={{ padding: '14px 24px' }}>
                    Comenzar a leer
                  </Link>
                  <Link href="/iniciar-sesion" className="btn btn-ghost" style={{ padding: '14px 24px' }}>
                    Iniciar sesión
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="m-hide-lg" style={{ position: 'relative', height: 420 }}>
            {covers[0] && (
              <div style={{ position: 'absolute', left: 0, bottom: 0, transform: 'rotate(-3deg)' }}>
                <BookCover book={covers[0]} w={170} />
              </div>
            )}
            {covers[1] && (
              <div style={{ position: 'absolute', left: 130, bottom: 24, zIndex: 2 }}>
                <BookCover book={covers[1]} w={220} />
              </div>
            )}
            {covers[2] && (
              <div style={{ position: 'absolute', left: 300, bottom: 8, transform: 'rotate(4deg)' }}>
                <BookCover book={covers[2]} w={180} />
              </div>
            )}
          </div>
        </div>

        <div
          className="m-cols-2"
          style={{
            marginTop: 80,
            paddingTop: 32,
            borderTop: '1px solid var(--rule)',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 40,
          }}
        >
          {stats.map(([n, l], i) => (
            <div key={i}>
              <div className="serif" style={{ fontSize: 36, lineHeight: 1, letterSpacing: '-0.02em' }}>{n}</div>
              <div style={{ marginTop: 8, color: 'var(--stone)', fontSize: 13 }}>{l}</div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}
